import {
  IconHome,
  IconReceipt2,
  IconRadar2,
  IconChartBar,
  IconHelp,
  IconTicket,
  IconClipboardText,
  IconTruck,
  IconBrandWhatsapp,
  IconPhone,
  IconMail,
  IconCalendar,
  IconCalendarEvent,
  IconClock,
  IconPackage,
  IconGitFork,
  IconClipboardCheck,
  IconBriefcase,
  IconBuildingCommunity,
  IconTool,
  IconAddressBook,
  IconUserPlus,
  IconMapPin,
  IconBuildingWarehouse,
  IconMapSearch,
  IconCurrencyEuro,
  IconAlertTriangle,
  IconBarcode,
  IconArrowsExchange,
  IconShoppingCart,
  IconPackageExport,
  IconArrowsLeftRight,
  IconTruckDelivery,
  IconPackageImport,
  IconClipboardList,
  IconUser,
  IconFileText,
  IconCar,
  IconBookmarks,
  IconTrash,
  IconShield,
  IconShieldExclamation,
  IconUsers,
  IconFiles,
  IconHandStop,
  IconRobot,
  IconRefresh,
  IconWebhook,
  IconMailOpened,
  IconSettings,
  IconChecklist,
  IconTransfer,
  IconDatabase,
  IconUrgent,
  IconBriefcase2,
  IconBook,
  IconUserCheck,
  IconWorldWww,
  IconWorld,
} from '@tabler/icons-react';

export type PortalRole = 'TECHNICIAN' | 'TEAMLEAD' | 'ADMIN';

/** Ein einzelnes Navigationsziel (Blatt). */
export interface NavLeaf {
  icon: typeof IconHome;
  label: string;
  path: string;
  /** Zusätzliche Suchbegriffe (Synonyme, Abkürzungen) für die Command-Palette. */
  keywords?: string;
  /** Nur für Admins sichtbar — für Admin-Ziele innerhalb einer allgemeinen Gruppe. */
  adminOnly?: boolean;
  /** Ergänzt adminOnly: auch Teamleads dürfen dieses Ziel sehen. */
  teamleadOk?: boolean;
}

/** Optional benannte Unter-Sektion innerhalb einer Gruppe. */
export interface NavSection {
  label?: string;
  items: NavLeaf[];
}

/** Eine aufklappbare Hauptgruppe der Sidebar. */
export interface NavGroup {
  id: string;
  label: string;
  icon: typeof IconHome;
  sections: NavSection[];
  /** Nur für Admins sichtbar. */
  adminOnly?: boolean;
  /** Ergänzt adminOnly: Teamleads sehen die Gruppe mit den Blättern, die ebenfalls teamleadOk sind. */
  teamleadOk?: boolean;
}

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'start',
    label: 'Start',
    icon: IconHome,
    sections: [
      {
        items: [
          { icon: IconHome, label: 'Dashboard', path: '/', keywords: 'übersicht home' },
          {
            icon: IconRadar2,
            label: 'Lagebericht',
            path: '/briefing',
            keywords: 'kian lage briefing übersicht prioritäten dispatcher sla offen tageslage persönlich techniker coach',
          },
          {
            icon: IconRobot,
            label: 'KIAN',
            path: '/kian',
            keywords:
              'kian assistent ki chat fragen gespräch kunde projekt ticket kontext anlegen ändern schließen import datei json csv excel liste',
          },
          {
            icon: IconChartBar,
            label: 'Statistik',
            path: '/statistik',
            keywords: 'statistik kennzahlen metriken auswertung zahlen team tickets stunden zeiterfassung rückrufe',
          },
          { icon: IconHelp, label: 'Hilfe & FAQ', path: '/help/faq', keywords: 'support anleitung' },
          {
            icon: IconBook,
            label: 'Wissensdatenbank',
            path: '/knowledge-base',
            keywords: 'wissen wiki artikel anleitung howto how-to sop playbook dokumentation lösung',
          },
        ],
      },
    ],
  },
  {
    id: 'work',
    label: 'Tickets & Einsätze',
    icon: IconTicket,
    sections: [
      {
        items: [
          {
            icon: IconTicket,
            label: 'Tickets',
            path: '/tickets',
            keywords: 'support anfrage liste disposition dispatch planung workload kanban board',
          },
          {
            icon: IconCalendarEvent,
            label: 'Kalender',
            path: '/calendar',
            keywords:
              'kalender teamkalender termin termine jour fixe besprechung meeting projekttermin schulung team outlook urlaub abwesenheit rufbereitschaft wer ist wann wo',
          },
          {
            icon: IconChecklist,
            label: 'Aufgaben & Erinnerungen',
            path: '/reminders',
            keywords: 'aufgabe aufgaben todo to-do erinnerung wiedervorlage follow-up nachfassen rückruf zuweisen fällig glocke reminder',
          },
          {
            icon: IconTrash,
            label: 'Ticket-Papierkorb',
            path: '/tickets/trash',
            adminOnly: true,
            keywords: 'papierkorb geloescht gelöscht löschen entfernt wiederherstellen trash muell müll',
          },
          {
            icon: IconTransfer,
            label: 'Übergaben',
            path: '/handovers',
            keywords:
              'uebergabe übergabe übernahme handover verteilt zugewiesen angenommen abgelehnt disposition',
          },
          { icon: IconClipboardText, label: 'Service Report', path: '/service-report', keywords: 'servicebericht einsatzbericht' },
          { icon: IconTruck, label: 'Delivery Report', path: '/delivery-report', keywords: 'liefernachweis lieferbericht' },
          // Steht neben den beiden Formularen, nicht bei Beschaffung: Wer einen
          // abgeschlossenen Bericht sucht, sucht ihn dort, wo er ihn geschrieben hat.
          { icon: IconClipboardCheck, label: 'Einsatz-Archiv', path: '/einsatz-archiv', keywords: 'historie abgeschlossen servicebericht service report lieferschein liefernachweis delivery berichte pdf belegnummer' },
        ],
      },
      {
        label: 'Triage',
        items: [
          { icon: IconPhone, label: 'Anrufer & Rückrufe', path: '/admin/callers', keywords: 'caller anruf telefon unbekannt rückruf rueckruf zurückrufen wiedervorlage kian' },
          { icon: IconBrandWhatsapp, label: 'Chat-Inbox', path: '/chats', keywords: 'chat whatsapp chatwerk nachricht messenger inbox kian' },
          { icon: IconMail, label: 'Tickets ohne Kunde', path: '/admin/ticket-sender-triage', keywords: 'sender zuordnung mail' },
          { icon: IconBuildingCommunity, label: 'Kundenanlagen', path: '/admin/kundenanlagen', keywords: 'kunde anlegen vorschlag freigabe weclapp' },
          { icon: IconUserPlus, label: 'AP-Vorschläge', path: '/contact-suggestions', keywords: 'ansprechpartner vorschlag freigabe' },
          { icon: IconMapPin, label: 'Adress-Vorschläge', path: '/admin/address-suggestions', keywords: 'adresse anschrift vorschlag freigabe stammdaten weclapp' },
          { icon: IconChartBar, label: 'KIAN-Nutzung', path: '/admin/kian-usage', keywords: 'kian kosten budget nutzung verbrauch techniker' },
          { icon: IconShieldExclamation, label: 'KIAN Injection-Log', path: '/admin/kian-injections', keywords: 'kian injection prompt sicherheit security log' },
        ],
      },
      {
        label: 'Planung & Material',
        items: [
          { icon: IconBriefcase2, label: 'Projekte', path: '/projects', keywords: 'projekt projektverwaltung vorlage template tomedo wechsel umstellung umzug fotos vorher nachher aufgaben' },
          { icon: IconCalendar, label: 'Terminplanung', path: '/appointments', keywords: 'termine kundentermin vorschlag kundenportal' },
          { icon: IconClock, label: 'Zeitbuchung', path: '/time-booking', keywords: 'zeiterfassung stunden' },
          {
            icon: IconUserCheck,
            label: 'Anwesenheit',
            path: '/anwesenheit',
            keywords: 'stempeln einstempeln ausstempeln pause jibble anwesend team jetzt wer arbeitet eingeloggt arbeitszeit',
          },
          { icon: IconUrgent, label: 'Rufbereitschaft', path: '/rufbereitschaft', keywords: 'bereitschaft rufbereitschaft notdienst notfall alarm sms wochenende nacht' },
          { icon: IconPackage, label: 'Materialanfragen', path: '/technician-requests', keywords: 'material bestellung anforderung' },
          { icon: IconGitFork, label: 'Beschaffung', path: '/procurement', keywords: 'einkauf pipeline' },
        ],
      },
    ],
  },
  {
    id: 'customers',
    label: 'Kunden',
    icon: IconBriefcase,
    sections: [
      {
        items: [
          { icon: IconBriefcase, label: 'Kunden-Hub', path: '/customer-hub', keywords: 'angebot workflow' },
          { icon: IconReceipt2, label: 'Aufträge', path: '/auftraege', keywords: 'aufträge angebote angenommen controlhub' },
          { icon: IconBuildingCommunity, label: 'Infrastruktur', path: '/infrastructure', keywords: 'assets geräte netzwerk' },
          { icon: IconWorldWww, label: 'DNS-Zonen', path: '/dns-zonen', keywords: 'dns zone domain cloudflare nameserver record mx txt spf dkim a cname wiederherstellen bind export' },
          { icon: IconWorld, label: 'Domains', path: '/domains', keywords: 'domain autodns internetx ablauf verlängerung auto-renew registrar nameserver kündigung' },
          { icon: IconTool, label: 'Wartungskunden', path: '/maintenance', keywords: 'wartung vertrag' },
          { icon: IconAddressBook, label: 'Kontaktverzeichnis', path: '/contacts', keywords: 'ansprechpartner adressen' },
        ],
      },
    ],
  },
  {
    id: 'warehouse',
    label: 'Lager & Material',
    icon: IconBuildingWarehouse,
    sections: [
      {
        label: 'Bestand',
        items: [
          { icon: IconBuildingWarehouse, label: 'Lager-Übersicht', path: '/inventory', keywords: 'warehouse lager' },
          { icon: IconMapSearch, label: 'Bestandssuche', path: '/inventory/search', keywords: 'suche artikel finden' },
          { icon: IconPackage, label: 'Artikelstamm', path: '/inventory/articles', keywords: 'artikel produkt' },
          {
            icon: IconPackage,
            label: 'Artikel prüfen',
            path: '/inventory/article-review',
            keywords: 'artikel prüfen freigabe technikeranlage zusammenführen merge',
            adminOnly: true,
          },
          { icon: IconCurrencyEuro, label: 'Bestandswert', path: '/inventory/valuation', keywords: 'wert bewertung euro' },
          { icon: IconAlertTriangle, label: 'Unterbestand', path: '/inventory/low-stock', keywords: 'mindestbestand knapp' },
          { icon: IconBarcode, label: 'SNR-Lebenslauf', path: '/inventory/serials', keywords: 'seriennummer serial historie' },
          { icon: IconArrowsExchange, label: 'Bestandsbewegungen', path: '/inventory/movements', keywords: 'bewegungen buchungen' },
        ],
      },
      {
        label: 'Wareneingang',
        items: [
          { icon: IconShoppingCart, label: 'Einkaufsbestellungen', path: '/purchase-orders', keywords: 'bestellung purchase order po' },
          { icon: IconPackageImport, label: 'Warenannahme', path: '/goods-receipts', keywords: 'wareneingang annahme empfang' },
        ],
      },
      {
        label: 'Warenausgang',
        items: [
          { icon: IconPackageExport, label: 'Warenausgang', path: '/inventory/issue', keywords: 'ausgabe entnahme' },
          { icon: IconArrowsLeftRight, label: 'Umlagerung', path: '/inventory/transfer', keywords: 'transfer umbuchen' },
          { icon: IconTruckDelivery, label: 'Auslieferungsaufträge', path: '/inventory/delivery-orders', keywords: 'lieferung versand' },
        ],
      },
      {
        label: 'Rückgabe & Inventur',
        items: [
          { icon: IconArrowsExchange, label: 'Rückgabe', path: '/inventory/returns', keywords: 'retoure zurück' },
          { icon: IconClipboardList, label: 'Rückgaben prüfen', path: '/inventory/returns/review', keywords: 'retoure prüfung review' },
          { icon: IconClipboardCheck, label: 'Inventur starten', path: '/inventory/inventory-start', keywords: 'inventur zählung' },
        ],
      },
    ],
  },
  {
    id: 'personal',
    label: 'Persönlich',
    icon: IconUser,
    sections: [
      {
        items: [
          { icon: IconFileText, label: 'Submissions', path: '/submissions', keywords: 'einreichungen berichte meine' },
          { icon: IconCar, label: 'Fahrten', path: '/my-trips', keywords: 'reisen km fahrtenbuch' },
          { icon: IconBookmarks, label: 'Tools-Library', path: '/tools', keywords: 'werkzeuge software' },
          { icon: IconTrash, label: 'Papierkorb', path: '/trash', keywords: 'gelöscht trash' },
        ],
      },
    ],
  },
  {
    id: 'admin',
    label: 'Administration',
    icon: IconShield,
    adminOnly: true,
    teamleadOk: true,
    sections: [
      {
        items: [
          { icon: IconUsers, label: 'Benutzer', path: '/admin/users', keywords: 'user accounts' },
          { icon: IconFiles, label: 'Belege & Dokumente', path: '/admin/belege', keywords: 'dokumente belege' },
          {
            icon: IconReceipt2,
            label: 'Leistungsjournal',
            path: '/admin/leistungsjournal',
            keywords: 'abrechnung buchhaltung leistungen zeiten material anfahrt km journal controlhub export csv',
          },
          { icon: IconAlertTriangle, label: 'Submissions', path: '/admin/submissions', keywords: 'einreichungen admin', teamleadOk: true },
          { icon: IconShoppingCart, label: 'Shop-Katalog', path: '/admin/shop', keywords: 'shop katalog produkte' },
          { icon: IconHelp, label: 'Kundensupport', path: '/admin/customer-portal', keywords: 'kundenportal support' },
          { icon: IconBook, label: 'Wissensdatenbank-Freigabe', path: '/admin/knowledge-base', keywords: 'wissen wiki artikel freigabe genehmigen ablehnen' },
        ],
      },
      {
        label: 'Fahrten',
        items: [
          { icon: IconCar, label: 'Fahrten', path: '/admin/trips', keywords: 'reisen km admin' },
          { icon: IconHandStop, label: 'Manuelle Fahrten', path: '/admin/manual-trips', keywords: 'manuell fahrten' },
        ],
      },
      {
        label: 'Material',
        items: [
          { icon: IconPackage, label: 'Materialanfragen', path: '/admin/technician-requests', keywords: 'material admin' },
          { icon: IconBookmarks, label: 'Tools-Library', path: '/admin/tools', keywords: 'werkzeuge admin' },
        ],
      },
      {
        label: 'KIAN & KI',
        items: [
          {
            icon: IconRobot,
            label: 'KIAN-Verwaltung',
            path: '/admin/kian',
            keywords: 'ki ai assistent llm analytics analyse statistik kontext strategie metriken health',
          },
        ],
      },
      {
        label: 'System',
        items: [
          { icon: IconRefresh, label: 'Weclapp Sync', path: '/admin/sync', keywords: 'synchronisation sync' },
          { icon: IconDatabase, label: 'Weclapp Mirror', path: '/admin/weclapp-mirror', keywords: 'mirror spiegel servicehub lesepfad backfill' },
          { icon: IconMailOpened, label: 'Mail-Eingang (Schatten)', path: '/admin/mail-intake', keywords: 'mail eingang postfach mail2ticket schattenbetrieb posteingang' },
          { icon: IconWebhook, label: 'Webhook-Logs', path: '/admin/webhook-logs', keywords: 'webhook logs' },
          { icon: IconMailOpened, label: 'E-Mail Versand-Log', path: '/admin/email-logs', keywords: 'mail logs versand' },
          { icon: IconMail, label: 'E-Mail Vorschauen', path: '/admin/email-previews', keywords: 'mail vorschau templates' },
          { icon: IconUrgent, label: 'Rufbereitschaft', path: '/admin/rufbereitschaft', keywords: 'bereitschaft rufbereitschaft notdienst notfall alarm sms rotation seven abrechnung' },
          { icon: IconSettings, label: 'Einstellungen', path: '/admin/settings', keywords: 'settings konfiguration jibble zeiterfassung stempel' },
        ],
      },
    ],
  },
];

/**
 * Feste Schnellzugriff-Ziele – stehen IMMER oben in der Sidebar,
 * unabhängig von Favoriten/Verlauf.
 */
export const QUICK_LINKS: NavLeaf[] = [
  { icon: IconTicket, label: 'Alle Tickets', path: '/tickets?tab=all', keywords: 'tickets liste alle' },
  { icon: IconUser, label: 'Meine Tickets', path: '/tickets?tab=mine', keywords: 'meine tickets mine zugewiesen' },
  { icon: IconClipboardText, label: 'Service Report', path: '/service-report', keywords: 'servicebericht einsatzbericht' },
  { icon: IconTruck, label: 'Delivery Report', path: '/delivery-report', keywords: 'liefernachweis lieferbericht' },
];

/** Basis-Pfade der Schnellzugriff-Ziele (ohne Query) – für Dedupe in Favoriten/Verlauf. */
export const QUICK_LINK_BASE_PATHS = new Set(QUICK_LINKS.map((l) => l.path.split('?')[0]));

/**
 * Gruppen für die aktuelle Rolle (filtert adminOnly heraus — auf Gruppen- UND
 * Blatt-Ebene). Teamleads sehen adminOnly-Ziele nur, wenn sie `teamleadOk` sind;
 * in einer adminOnly-Gruppe gilt das für jedes Blatt einzeln.
 */
export function getNavGroups(isAdmin: boolean, isTeamlead = false): NavGroup[] {
  const canSee = (adminOnly?: boolean, teamleadOk?: boolean) =>
    !adminOnly || isAdmin || (isTeamlead && !!teamleadOk);
  return NAV_GROUPS.filter((g) => canSee(g.adminOnly, g.teamleadOk))
    .map((g) => ({
      ...g,
      sections: g.sections
        .map((s) => ({
          ...s,
          items: s.items.filter((i) => canSee(i.adminOnly || g.adminOnly, i.teamleadOk)),
        }))
        .filter((s) => s.items.length > 0),
    }))
    .filter((g) => g.sections.length > 0);
}

/** Flache Liste aller erreichbaren Ziele — für Palette, Favoriten-Lookup etc. */
export function getAllLeaves(isAdmin: boolean, isTeamlead = false): NavLeaf[] {
  return getNavGroups(isAdmin, isTeamlead).flatMap((g) => g.sections.flatMap((s) => s.items));
}

/** Ziel zu einem Pfad finden (für Favoriten- und Verlaufs-Anzeige). */
export function findLeafByPath(path: string, isAdmin: boolean, isTeamlead = false): NavLeaf | undefined {
  return getAllLeaves(isAdmin, isTeamlead).find((l) => l.path === path);
}

/**
 * Prüft, ob ein Nav-Ziel zur aktuellen Route aktiv ist.
 * Übernimmt das bisherige Matching-Verhalten aus AppLayout.
 */
export function isLeafActive(leafPath: string, currentPath: string): boolean {
  if (currentPath === leafPath) return true;

  // /tickets: aktiv auf /tickets und /tickets/new, NICHT auf /tickets/board
  if (leafPath === '/tickets') {
    return currentPath === '/tickets' || currentPath.startsWith('/tickets/new');
  }

  // Pfade, die NUR exakt matchen (Unterseiten haben eigene Nav-Einträge)
  const exactMatchPaths = ['/inventory', '/inventory/returns'];
  if (exactMatchPaths.includes(leafPath)) {
    return currentPath === leafPath;
  }

  return currentPath.startsWith(leafPath + '/');
}

/** ID der Gruppe, die zur aktuellen Route gehört (für Auto-Expand). */
export function findActiveGroupId(currentPath: string, isAdmin: boolean, isTeamlead = false): string | undefined {
  for (const group of getNavGroups(isAdmin, isTeamlead)) {
    for (const section of group.sections) {
      if (section.items.some((item) => isLeafActive(item.path, currentPath))) {
        return group.id;
      }
    }
  }
  return undefined;
}
